import { useCallback, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, UploadCloud, X, Check, Loader2, AlertCircle, Plus } from "lucide-react";
import { toast } from "sonner";
import { adminAnalyzeImage, adminUploadImage, adminWaitForUploadReady, adminCreateGallery } from "@/lib/api";
import { autoResizeImage } from "@/lib/imageResize";

const CONCURRENCY = 2;

const slugify = (s = "") =>
  s
    .replace(/\.[^.]+$/, "")
    .replace(/[_\-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim();

export default function AdminGalleryBulkUpload() {
  const fileInput = useRef(null);
  const [client, setClient] = useState("");
  const [items, setItems] = useState([]); // {id, file, preview, status, progress, message, gallery_id, title}
  const [running, setRunning] = useState(false);

  const addFiles = useCallback((files) => {
    const arr = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
    if (arr.length === 0) return;
    const next = arr.map((f) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      file: f,
      preview: URL.createObjectURL(f),
      original_name: slugify(f.name),
      status: "pending", // pending | analysing | uploading | done | error
      progress: 0,
      message: "",
      title: "",
    }));
    setItems((prev) => [...prev, ...next]);
  }, []);

  const onDrop = (e) => {
    e.preventDefault();
    addFiles(e.dataTransfer.files);
  };

  const removeItem = (id) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
  };

  const clearDone = () => {
    setItems((prev) => prev.filter((it) => it.status !== "done"));
  };

  const processOne = async (item, mutate) => {
    try {
      let workFile = item.file;
      let resizedNote = "";
      try {
        const r = await autoResizeImage(item.file);
        if (r.resized) {
          resizedNote = "Auto-resized to fit, ";
          workFile = r.file;
        }
      } catch {
        // fall back to original file
      }
      mutate(item.id, { status: "analysing", message: `${resizedNote}AI is reading the image…` });
      const ai = await adminAnalyzeImage(workFile, {
        original_name: item.original_name,
        client,
      });
      mutate(item.id, { status: "uploading", message: "Uploading to storage…" });
      const uploaded = await adminUploadImage(workFile);
      if (!uploaded?.success) {
        throw new Error(uploaded?.error || "Upload failed");
      }
      mutate(item.id, { status: "uploading", message: "Processing image…" });
      const ready = await adminWaitForUploadReady(uploaded.imageId, { intervalMs: 1500, timeoutMs: 45000 });
      const thumbUrl = ready.thumbnail_url || uploaded.url;
      const created = await adminCreateGallery({
        type: "photo",
        title: ai.title || item.original_name || "Untitled",
        original_name: item.original_name,
        client: client.trim(),
        category: ai.category || "General",
        size: ai.suggested_size || "standard",
        image: uploaded.url,
        thumbnail: thumbUrl,
        description: ai.description || "",
        alt_text: ai.alt_text || "",
        status: "draft",
        featured: false,
      });
      mutate(item.id, {
        status: "done",
        progress: 100,
        message: `Drafted as "${created.title}"`,
        gallery_id: created.id,
        title: created.title,
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || "Failed";
      mutate(item.id, { status: "error", message: typeof detail === "string" ? detail : "Failed" });
    }
  };

  const start = async () => {
    if (items.length === 0) {
      toast.error("Add at least one image first.");
      return;
    }
    setRunning(true);

    // Build a queue of items to process
    const queue = [...items].filter((it) => it.status === "pending" || it.status === "error");

    const mutate = (id, patch) =>
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));

    // Mark all queued as pending first
    queue.forEach((q) => mutate(q.id, { status: "pending", message: "Queued…" }));

    let cursor = 0;
    const workers = Array.from({ length: Math.min(CONCURRENCY, queue.length) }).map(async () => {
      while (cursor < queue.length) {
        const i = cursor++;
        if (i >= queue.length) break;
        await processOne(queue[i], mutate);
      }
    });
    await Promise.all(workers);

    setRunning(false);
    toast.success("Batch complete, drafts ready in the gallery.");
  };

  const total = items.length;
  const doneCount = items.filter((i) => i.status === "done").length;
  const errCount = items.filter((i) => i.status === "error").length;

  return (
    <div className="px-6 lg:px-10 py-10 max-w-6xl">
      <Link
        to="/admin/gallery"
        className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase text-[#A3A3A3] hover:text-white mb-6"
      >
        <ArrowLeft size={14} /> Back to gallery
      </Link>

      <div className="text-[10px] tracking-[0.32em] uppercase text-[#4A6D8C] mb-2">
        Bulk upload
      </div>
      <h1 className="font-display text-4xl tracking-tight">Drop a batch</h1>
      <p className="text-[13px] text-[#A3A3A3] mt-3 max-w-2xl">
        Drop multiple images at once. We&apos;ll AI-analyse each one (using the
        filename as the original name) and create them as drafts you can review
        and publish from the gallery.
      </p>

      <div className="mt-6 max-w-md">
        <label className="text-[10px] tracking-[0.28em] uppercase text-[#737373]">
          Client name (applied to every photo in this batch - optional)
        </label>
        <input
          type="text"
          value={client}
          onChange={(e) => setClient(e.target.value)}
          placeholder="e.g. EKFB"
          className="hl-input"
          data-testid="bulk-client"
        />
      </div>

      <div
        onClick={() => fileInput.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        data-testid="bulk-dropzone"
        className="mt-8 border border-dashed border-[#404040] hover:border-[#4A6D8C] transition-colors py-16 flex flex-col items-center justify-center gap-3 cursor-pointer bg-[#0a0a0a] text-center px-6"
      >
        <UploadCloud size={28} className="text-[#658CAE]" />
        <div className="font-display text-lg">Drop multiple images</div>
        <div className="text-[12px] text-[#A3A3A3]">
          or click to browse - JPG / PNG / WEBP. Oversized images are auto-resized to fit.
        </div>
        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          multiple
          hidden
          data-testid="bulk-file-input"
          onChange={(e) => addFiles(e.target.files)}
        />
      </div>

      {/* Queue */}
      {items.length > 0 && (
        <div className="mt-8">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div className="text-[12px] tracking-[0.18em] uppercase text-[#A3A3A3]">
              Queue · {total} item{total !== 1 ? "s" : ""}
              {doneCount > 0 && <span className="text-[#658CAE]"> · {doneCount} done</span>}
              {errCount > 0 && <span className="text-red-300"> · {errCount} failed</span>}
            </div>
            <div className="flex gap-3">
              {doneCount > 0 && !running && (
                <button onClick={clearDone} className="hl-btn-ghost text-[11px]" data-testid="bulk-clear-done">
                  Clear done
                </button>
              )}
              <button
                onClick={start}
                disabled={running}
                data-testid="bulk-start"
                className="hl-btn-primary text-[12px] disabled:opacity-60"
              >
                {running ? "Processing…" : "Start batch"}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="bulk-queue">
            {items.map((it) => (
              <div
                key={it.id}
                data-testid={`bulk-item-${it.id}`}
                className="bg-[#0f0f0f] border border-[#1a1a1a] flex flex-col"
              >
                <div className="aspect-[4/3] bg-[#0a0a0a] relative overflow-hidden">
                  <img src={it.preview} alt={it.original_name} className="w-full h-full object-cover" />
                  {it.status === "done" && (
                    <span className="absolute top-2 left-2 text-[9px] tracking-[0.22em] uppercase bg-[#4A6D8C] px-2 py-1 inline-flex items-center gap-1">
                      <Check size={10} /> Draft
                    </span>
                  )}
                  {it.status === "error" && (
                    <span className="absolute top-2 left-2 text-[9px] tracking-[0.22em] uppercase bg-red-500/80 px-2 py-1 inline-flex items-center gap-1">
                      <AlertCircle size={10} /> Failed
                    </span>
                  )}
                  {(it.status === "analysing" || it.status === "uploading") && (
                    <span className="absolute top-2 left-2 text-[9px] tracking-[0.22em] uppercase bg-black/60 border border-white/15 px-2 py-1 inline-flex items-center gap-1 backdrop-blur-md">
                      <Loader2 size={10} className="animate-spin" /> {it.status}
                    </span>
                  )}
                  {it.status === "pending" && (
                    <button
                      onClick={() => removeItem(it.id)}
                      className="absolute top-2 right-2 bg-black/60 border border-white/15 backdrop-blur-md p-1.5"
                      aria-label="Remove"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
                <div className="p-3 text-[12px]">
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[#737373] mb-1">
                    Original
                  </div>
                  <div className="font-display text-sm tracking-tight line-clamp-1">
                    {it.title || it.original_name || it.file.name}
                  </div>
                  <div className="text-[11px] text-[#A3A3A3] mt-1 min-h-[18px]">
                    {it.message || "Ready"}
                  </div>
                  {it.status === "done" && it.gallery_id && (
                    <Link
                      to={`/admin/gallery/${it.gallery_id}`}
                      className="text-[11px] tracking-[0.18em] uppercase text-[#658CAE] hover:text-white mt-2 inline-block"
                    >
                      Open in editor →
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
